import React, { useState, useRef, useEffect } from 'react';

export default function NumberDropdown({ 
  onSelect, 
  initialValue = 1,
  min = 1,
  max = 100,
  disabled = false,
  className = ""
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState(initialValue);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);


  useEffect(() => {
    setSelectedNumber(initialValue);
  }, [initialValue]);

  // Закрытие dropdown при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (number) => {
    setSelectedNumber(number);
    setIsOpen(false);
    if (onSelect) {
      onSelect(number);
    }
  };

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  // Прокрутка к выбранному элементу при открытии
  useEffect(() => {
    if (isOpen && listRef.current) {
      const selectedElement = listRef.current.querySelector(`[data-value="${selectedNumber}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [isOpen, selectedNumber]);

  // Генерируем массив чисел от min до max
  const numbers = Array.from({ length: max - min + 1 }, (_, i) => i + min);

  return (
    <div className={`relative inline-block ${className}`} ref={dropdownRef}>
      {/* Кнопка С ЧЕТКОЙ ВИДИМОЙ РАМКОЙ */}
      <button
        onClick={toggleDropdown}
        disabled={disabled}
        className={`min-w-[40px] px-2 py-1 flex items-center justify-between gap-2 rounded-md border-2 transition-all
          ${disabled 
            ? 'cursor-not-allowed opacity-50 text-gray-400 border-gray-300 bg-gray-50' 
            : isOpen
              ? 'cursor-pointer text-gray-700 border-blue-500 bg-blue-50 shadow-sm'
              : 'cursor-pointer text-gray-700 border-gray-400 bg-white hover:border-blue-400 hover:bg-gray-50'
          }`}
      >
        <span className="font-medium">{selectedNumber}</span>
        <svg
          className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Выпадающий список */}
      {isOpen && (
        <div className="absolute z-50 min-w-[120px] mt-1 bg-white border-2 border-blue-500 rounded-lg shadow-xl animate-fadeIn">
          {/* Контейнер с фиксированной высотой и прокруткой */}
          <div 
            ref={listRef}
            className="max-h-64 overflow-y-auto custom-scrollbar"
          >
            {numbers.map((number) => (
              <button
                key={number}
                data-value={number}
                onClick={() => handleSelect(number)}
                className={`w-full px-4 py-2 text-left transition-colors duration-150 flex items-center justify-between
                  ${selectedNumber === number
                    ? 'bg-blue-500 text-white font-semibold'
                    : 'text-gray-700 hover:bg-blue-50'
                  }`}
              >
                <span>{number}</span>
                {selectedNumber === number && (
                  <svg 
                    className="w-4 h-4" 
                    fill="currentColor" 
                    viewBox="0 0 20 20"
                  >
                    <path 
                      fillRule="evenodd" 
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" 
                      clipRule="evenodd" 
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <style jsx>{`
        /* Скрыть скроллбар для Chrome, Safari и Opera */
        .custom-scrollbar::-webkit-scrollbar {
          display: none;
        }

        /* Скрыть скроллбар для Firefox */
        .custom-scrollbar {
          scrollbar-width: none;
        }

        /* Скрыть скроллбар для IE и Edge */
        .custom-scrollbar {
          -ms-overflow-style: none;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.15s ease-out;
        }
      `}</style>
    </div>
  );
}